import { useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';

import { api, API_URL } from '../api/client';
import { useSessionStore } from '../api/session';
import { setMutationFlusher } from './mutations';
import { currentDeviceUuid, tenantScopeKey, tenantScopeOf } from './session-binding';
import { SyncContext, type SyncContextValue } from './sync-context';
import { createSyncApi } from './sync-api';
import { INITIAL_SYNC_STATE, SyncEngine, type SyncState } from './sync-engine';
import { activateTenantDb, getTenantDb, useTenantDb, type TenantDatabase } from './tenant-db';

export interface SyncProviderProps {
    children: ReactNode;
    /** Alleen voor tests: een engine met een eigen `fetch`. */
    createEngine?: (db: TenantDatabase) => SyncEngine;
}

const idle = () => () => {};
const initial = () => INITIAL_SYNC_STATE;

/**
 * Bindt de sessie aan de offline opslag:
 *
 * - de omgeving uit de sessie (`session.tenant.uuid`, van `session.user.uuid`)
 *   bepaalt welke database open staat; wisselen sluit de ene en opent de
 *   andere, uitloggen sluit alles;
 * - per open database draait precies één sync-engine;
 * - een nieuw token (inloggen, wisselen, opstarten met een bewaarde sessie)
 *   start een bootstrap.
 */
export function SyncProvider({ children, createEngine }: SyncProviderProps) {
    const scopeKey = useSessionStore((state) =>
        tenantScopeKey(tenantScopeOf(state.session, state.impersonation !== null)),
    );
    const token = useSessionStore((state) => state.session?.token ?? null);

    useEffect(() => {
        const { session, impersonation } = useSessionStore.getState();

        activateTenantDb(tenantScopeOf(session, impersonation !== null));
    }, [scopeKey]);

    const db = useTenantDb();

    const engine = useMemo(
        () =>
            db
                ? (createEngine?.(db) ??
                  new SyncEngine({
                      db,
                      api: createSyncApi(api, { baseUrl: API_URL }),
                      deviceUuid: currentDeviceUuid,
                  }))
                : null,
        [db, createEngine],
    );

    useEffect(() => {
        if (!engine) {
            return;
        }

        const stop = engine.start();
        /* Wijzigingen worden gebundeld: zie `SyncEngine.syncSoon()`. */
        setMutationFlusher(() => engine.syncSoon());

        return () => {
            setMutationFlusher(null);
            stop();
        };
    }, [engine]);

    useEffect(() => {
        /*
         * Bij wisselen van omgeving komt het nieuwe token één render eerder dan
         * de nieuwe engine. De engine van de vorige omgeving mag dan niet met
         * dat token bootstrappen: alleen de engine van de actieve database.
         */
        if (engine && token && getTenantDb() === engine.db) {
            void engine.requestBootstrap();
        }
    }, [engine, token]);

    const state: SyncState = useSyncExternalStore(
        engine?.subscribe ?? idle,
        engine?.getState ?? initial,
        engine?.getState ?? initial,
    );

    const value = useMemo<SyncContextValue>(
        () => ({
            state,
            engine,
            /* "Nu versturen" is een echte nieuwe poging: zie `SyncEngine.retryNow()`. */
            flushNow: async () => {
                await engine?.retryNow();
            },
            retryFailed: async () => {
                await engine?.retryNow();
            },
        }),
        [engine, state],
    );

    return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
